// ─── Global unhandled error & promise rejection handler ──────────────────

let isSetup = false;

export function setupGlobalErrorHandlers() {
  if (isSetup) return;
  isSetup = true;

  // ── Uncaught errors (synchronous) ──────────────────────────────────────
  window.addEventListener("error", (event) => {
    const error = event.error || event.message;

    console.error("[GlobalErrorHandler] Uncaught error:", error);

    // Prevent white screen — show a toast instead of crashing
    // (Only if the error doesn't come from our own boundary)
    if (event.filename?.includes("ErrorBoundary")) {
      return; // Already handled by boundary
    }

    // Log to monitoring service in production
    if (process.env.NODE_ENV === "production") {
      // Sentry.captureException(error);
    }

    // Don't let the default browser behavior show an error dialog
    event.preventDefault?.();
  });

  // ── Unhandled Promise rejections (async) ───────────────────────────────
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;

    console.error("[GlobalErrorHandler] Unhandled promise rejection:", error);

    if (process.env.NODE_ENV === "production") {
      // Sentry.captureException(error);
    }

    // Prevent default console warning in some browsers
    event.preventDefault?.();
  });
}

// ─── Safe async wrapper — wraps any promise with error handling ──────────
export async function safeAsync(promiseOrFn, options = {}) {
  const { fallback = null, onError = null, context = "unknown" } = options;

  try {
    const result =
      typeof promiseOrFn === "function" ? promiseOrFn() : promiseOrFn;
    return await result;
  } catch (error) {
    console.error(`[SafeAsync] Error in ${context}:`, error);

    if (onError) {
      onError(error);
    }

    if (process.env.NODE_ENV === "production") {
      // Sentry.captureException(error, { tags: { context } });
    }

    return fallback;
  }
}

// ─── API error extractor — consistently extract messages from axios errors ──
export function extractErrorMessage(
  error,
  defaultMessage = "Something went wrong",
) {
  if (!error) return defaultMessage;

  // Axios error
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.error) {
    return error.response.data.error;
  }

  // Network error
  if (error.message === "Network Error") {
    return "Unable to connect to server. Please check your internet connection.";
  }

  // Timeout
  if (error.code === "ECONNABORTED") {
    return "Request timed out. Please try again.";
  }

  // Generic error
  if (error.message) {
    return error.message;
  }

  return defaultMessage;
}

// ─── Graceful component error wrapper for event handlers ──────────────────
export function createSafeHandler(handler, options = {}) {
  const { onError, context = "event handler" } = options;

  return async (...args) => {
    try {
      return await handler(...args);
    } catch (error) {
      console.error(`[SafeHandler] Error in ${context}:`, error);

      if (onError) {
        onError(error);
      }

      if (process.env.NODE_ENV === "production") {
        // Sentry.captureException(error, { tags: { context } });
      }
    }
  };
}
